/**
 * Knowledge Base Editor Component
 * Modal for creating/editing AgentFunctionKnowledgeBase associations
 * (which knowledge-base collection(s) an AgentFunction may retrieve from)
 *
 * Unlike ParameterEditor (which only holds local state persisted later as part
 * of the parent AgentFunction's Parameters JSON blob), this entity is a real,
 * separately-persisted DB row with its own ID and CRUD endpoints -- Save calls
 * the API directly.
 */

import React, { useState, useEffect } from 'react';
import { ModalPortal } from '../../Modals/ModalPortal';
import LookupSelect from '../../Modals/LookupSelect';
import { agentFunctionKnowledgeBaseApiClient, AgentFunctionKnowledgeBase } from '@flow-studio/api';
import { lookupConfigs } from '../../../lookupConfigs';
import '../Agent/AgentEditor.css'; // Reuse agent editor styles

interface KnowledgeBaseEditorProps {
  isOpen: boolean;
  agentFunctionId: number;
  knowledgeBase?: AgentFunctionKnowledgeBase;
  onClose: () => void;
  onSaved: (knowledgeBase: AgentFunctionKnowledgeBase) => void;
}

const buildDefaultKnowledgeBase = (agentFunctionId: number): AgentFunctionKnowledgeBase => ({
  AgentFunctionKnowledgeBaseID: 0,
  AgentFunctionID: agentFunctionId,
  DocumentCollectionID: null,
  CollectionNameTemplate: '',
  Usage: 'Primary',
  Priority: 0,
  Profiles: '',
  IsActive: true,
  QueryRewriterName: null,
  RerankerName: null,
  RecentConversationTurnsCount: null,
});

export const KnowledgeBaseEditor: React.FC<KnowledgeBaseEditorProps> = ({
  isOpen,
  agentFunctionId,
  knowledgeBase,
  onClose,
  onSaved,
}) => {
  const [formData, setFormData] = useState<AgentFunctionKnowledgeBase>(buildDefaultKnowledgeBase(agentFunctionId));
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (knowledgeBase) {
        setFormData(knowledgeBase);
      } else {
        setFormData(buildDefaultKnowledgeBase(agentFunctionId));
      }
      setError(null);
    }
  }, [isOpen, knowledgeBase, agentFunctionId]);

  const handleChange = (field: keyof AgentFunctionKnowledgeBase, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const hasDocumentCollection = !!formData.DocumentCollectionID;
    const hasCollectionNameTemplate = !!formData.CollectionNameTemplate?.trim();

    if (!hasDocumentCollection && !hasCollectionNameTemplate) {
      setError('Either Document Collection or Collection Name Template is required');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        CollectionNameTemplate: formData.CollectionNameTemplate?.trim() || null,
        Profiles: formData.Profiles?.trim() || null,
      };

      const response = formData.AgentFunctionKnowledgeBaseID
        ? await agentFunctionKnowledgeBaseApiClient.update(formData.AgentFunctionKnowledgeBaseID, payload)
        : await agentFunctionKnowledgeBaseApiClient.create(payload);

      if (response.success && response.data) {
        onSaved(response.data);
        onClose();
      } else {
        setError(response.error?.message || 'Failed to save Knowledge Base association');
      }
    } catch (err: any) {
      setError(err.message || 'Error saving Knowledge Base association');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <ModalPortal>
      <div
        className="agent-editor-overlay"
        onClick={onClose}
      >
        <div
          className="parameter-editor-container"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="agent-editor-header">
            <h1 className="editor-title">
              {formData.AgentFunctionKnowledgeBaseID ? ' Edit Knowledge Base' : ' Add Knowledge Base'}
            </h1>
            <button className="close-btn" onClick={onClose}>
              ✕
            </button>
          </div>

          {error && (
            <div className="editor-error-banner">
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSave} className="editor-form">
            <div className="form-grid">
              <div className="form-group full">
                <LookupSelect
                  fetchConfig={lookupConfigs.documentCollection}
                  value={formData.DocumentCollectionID}
                  onChange={(value) => handleChange('DocumentCollectionID', value)}
                  label="Document Collection"
                  required={false}
                  placeholder="Select document collection (optional)..."
                  searchable={true}
                  maxItemsInDropdown={50}
                />
              </div>

              <div className="form-group full">
                <label>Collection Name Template</label>
                <input
                  type="text"
                  value={formData.CollectionNameTemplate || ''}
                  onChange={(e) => handleChange('CollectionNameTemplate', e.target.value)}
                  placeholder="forms-{tenantSlug}"
                  maxLength={200}
                />
              </div>

              <div className="form-group">
                <label>Usage *</label>
                <select
                  value={formData.Usage}
                  onChange={(e) => handleChange('Usage', e.target.value)}
                >
                  <option value="Primary">Primary</option>
                  <option value="Secondary">Secondary</option>
                  <option value="Fallback">Fallback</option>
                </select>
              </div>

              <div className="form-group">
                <label>Priority</label>
                <input
                  type="number"
                  value={formData.Priority}
                  onChange={(e) =>
                    handleChange('Priority', parseInt(e.target.value) || 0)
                  }
                  min="0"
                />
              </div>

              <div className="form-group full">
                <label>Profiles</label>
                <input
                  type="text"
                  value={formData.Profiles || ''}
                  onChange={(e) => handleChange('Profiles', e.target.value)}
                  placeholder="STARTUP,SMB (optional, comma-separated)"
                  maxLength={500}
                />
              </div>

              <div className="form-group">
                <label>Query Rewriter</label>
                <select
                  value={formData.QueryRewriterName || ''}
                  onChange={(e) => handleChange('QueryRewriterName', e.target.value || null)}
                >
                  <option value="">Default (NoOp)</option>
                  <option value="NoOp">NoOp</option>
                  <option value="Llm">Llm</option>
                </select>
              </div>

              <div className="form-group">
                <label>Reranker</label>
                <select
                  value={formData.RerankerName || ''}
                  onChange={(e) => handleChange('RerankerName', e.target.value || null)}
                >
                  <option value="">Default (NoOp)</option>
                  <option value="NoOp">NoOp</option>
                  <option value="Llm">Llm</option>
                </select>
              </div>

              <div className="form-group">
                <label>Recent Conversation Turns</label>
                <input
                  type="number"
                  value={formData.RecentConversationTurnsCount ?? ''}
                  onChange={(e) =>
                    handleChange('RecentConversationTurnsCount', e.target.value === '' ? null : parseInt(e.target.value) || 0)
                  }
                  placeholder="Default (3)"
                  min="0"
                />
              </div>

              <div className="checkbox-grid">
                <label className="checkbox-item">
                  <input
                    type="checkbox"
                    checked={formData.IsActive}
                    onChange={(e) =>
                      handleChange('IsActive', e.target.checked)
                    }
                  />
                  <span>Active</span>
                </label>
              </div>
            </div>

            <div className="editor-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
                disabled={saving}
              >
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </ModalPortal>
  );
};

export default KnowledgeBaseEditor;
